import React, { useMemo, useState } from "react";
import BookingSearch from "./components/BookingSearch";
import BookingSummaryCard from "./components/BookingSummaryCard";
import IntegrationClientCard from "./components/IntegrationClientCard";
import LogsInsightsRow from "./components/LogsInsightsRow";
import SearchDataSection from "./components/SearchDataSection";
import LogsSidebar from "./components/LogsSidebar";
import LogDetailsPanel from "./components/LogDetailsPanel";
import {
  extractCompanyFromLogs,
  parseLogsFromApiResponse,
  prepareDriverEvents,
} from "./utils";
import ThunksBooking from "../../../redux/actions/thunks/bookings";
import { connect } from "react-redux";
import "./styles.css";

const Index = ({
  token,
  getBookingDetailByRef,
}) => {
  const [bookingIdInput, setBookingIdInput] = useState("");
  const [searchedBookingId, setSearchedBookingId] = useState("");
  const [loading, setLoading] = useState(false);

  const [visibleLogs, setVisibleLogs] = useState([]);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [bookingRecord, setBookingRecord] = useState(null);

  const handleSearch = async () => {
    try {
      const normalizedInput = bookingIdInput.trim();
      setSearchedBookingId(normalizedInput);

      if (!normalizedInput) {
        setVisibleLogs([]);
        setSelectedLogId(null);
        setBookingRecord(null);
        return;
      }

      setLoading(true);

      const data = await getBookingDetailByRef(normalizedInput);

      if (!data || typeof data !== "object" || !data.id) {
        setVisibleLogs([]);
        setSelectedLogId(null);
        setBookingRecord(null);
        setLoading(false);
        return;
      }

      const parsedLogs = data.bookingLogs
        ? parseLogsFromApiResponse(data.bookingLogs)
        : [];
      setVisibleLogs(parsedLogs);
      setSelectedLogId(parsedLogs[parsedLogs.length - 1]?.id || null);
      setBookingRecord(data);
      setLoading(false);
    } catch (error) {
      console.error("Search Error:", error);
      setVisibleLogs([]);
      setSelectedLogId(null);
      setBookingRecord(null);
    } finally {
      setLoading(false);
    }
  };

  const selectedLog = useMemo(
    () => visibleLogs.find((item) => item.id === selectedLogId) || null,
    [visibleLogs, selectedLogId]
  );

  const sidebarLogs = useMemo(() => [...visibleLogs].reverse(), [visibleLogs]);

  const companyForSummary = useMemo(() => {
    if (bookingRecord?.company) return bookingRecord.company;
    return extractCompanyFromLogs(visibleLogs);
  }, [bookingRecord, visibleLogs]);

  const driverEventsPrepared = useMemo(
    () => prepareDriverEvents(bookingRecord?.driverEvents),
    [bookingRecord]
  );

  const hasBookingData = Boolean(bookingRecord?.id);
  const hasLogs = visibleLogs.length > 0;
  const hasSearchData =
    Array.isArray(bookingRecord?.searchData) &&
    bookingRecord.searchData.length > 0;
  const hasSearched = Boolean(searchedBookingId);

  return (
    <div className="logs-page">
      <BookingSearch
        value={bookingIdInput}
        onChange={setBookingIdInput}
        onSearch={handleSearch}
        loading={loading}
        searchedBookingId={searchedBookingId}
        hasBookingData={hasBookingData}
        hasLogs={hasLogs}
      />

      {hasSearched ? (
        hasBookingData ? (
          <>
            <BookingSummaryCard
              booking={bookingRecord}
              company={companyForSummary}
            />
            <IntegrationClientCard booking={bookingRecord} />
            <LogsInsightsRow
              booking={bookingRecord}
              driverEvents={driverEventsPrepared}
              logs={visibleLogs}
            />
            {hasLogs ? (
              <section className="logs-layout">
                <LogsSidebar
                  logs={sidebarLogs}
                  selectedLogId={selectedLogId}
                  onSelect={setSelectedLogId}
                />
                <LogDetailsPanel log={selectedLog} />
              </section>
            ) : null}
            {hasSearchData ? (
              <SearchDataSection
                searchId={bookingRecord.searchId}
                items={bookingRecord.searchData}
              />
            ) : null}
          </>
        ) : (
          <section className="logs-layout">
            <LogDetailsPanel log={null} />
          </section>
        )
      ) : null}
    </div>
  );
};



const mapState = (state) => ({
  token: state.userInfo.token,
});

const mapDispatch = {
  getBookingDetailByRef: ThunksBooking.getBookingDetailByRef,

};

export default connect(mapState, mapDispatch)(Index);
